import type {
    LayoutAlign,
    LayoutGridComponent,
    LayoutPageSettings,
    LayoutTrack,
} from '../../../models/layout-estimate-render-config';

export const SHEET_SIZES = {
    letter: { width: 816, height: 1056 },
    a4: { width: 794, height: 1123 },
} as const;

export const DEFAULT_ROW_HEIGHT = 32;
export const DEFAULT_GAP = 8;

const BAND_ALLOWANCE = 16;

export interface SheetGeometry {
    width: number;
    height: number;
    margin: number;
    rowHeight: number;
    gap: number;
    headerHeight: number;
    footerHeight: number;
    rows: number;
    bodyHeight: number;
}

export function sheetGeometry(
    page: LayoutPageSettings,
    header?: LayoutGridComponent,
    footer?: LayoutGridComponent,
): SheetGeometry {
    const size = SHEET_SIZES[page?.size] ?? SHEET_SIZES.letter;
    const margin = finite(page?.margin, 0);
    const rowHeight = Math.max(1, finite(page?.rowHeight, DEFAULT_ROW_HEIGHT));
    const gap = Math.max(0, finite(page?.gap, DEFAULT_GAP));
    const headerHeight = finite(header?.height, 0);
    const footerHeight = finite(footer?.height, 0);
    const available = size.height - 2 * margin - headerHeight - footerHeight - BAND_ALLOWANCE + gap;
    const rows = Math.max(0, Math.floor(available / (rowHeight + gap)));

    return {
        ...size,
        margin,
        rowHeight,
        gap,
        headerHeight,
        footerHeight,
        rows,
        bodyHeight: rows > 0 ? rows * rowHeight + (rows - 1) * gap : 0,
    };
}

export function trackToLine(track: LayoutTrack | undefined): string | undefined {
    if (!track) {
        return undefined;
    }

    const start = int(track.start);
    const span = int(track.span);
    const end = int(track.end);

    if (start !== undefined && end !== undefined) {
        return end > start ? `${start} / ${end}` : `${start} / span 1`;
    }

    if (start !== undefined) {
        return `${start} / span ${span ?? 1}`;
    }

    if (end !== undefined) {
        return `${Math.max(1, end - (span ?? 1))} / ${end}`;
    }

    if (span !== undefined) {
        return `auto / span ${span}`;
    }

    return undefined;
}

const FLEX: Record<LayoutAlign, string> = { start: 'flex-start', center: 'center', end: 'flex-end', stretch: 'stretch' };

export function flexAlign(value: LayoutAlign | undefined): string {
    return FLEX[value as LayoutAlign] ?? FLEX.start;
}

function finite(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function int(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : undefined;
}
